import React, { useState, useEffect, useRef } from "react";
import { usePublicAdmin } from "@/contexts/PublicAdminAuthContext";
import { toast } from "sonner";
import html2pdf from "html2pdf.js";
import { 
  FileText, Download, Printer, RefreshCw, Check, X, 
  ChevronLeft, User, Hash, Calendar, Award, AlertCircle, CheckCircle2 
} from "lucide-react";

// ---- DATA STRUCTURES ----
const BASE_DOCS = [
  "Admit Card / Hall Ticket of NEET UG 2026",
  "NEET UG 2026 Marksheet",
  "Nationality Certificate / Valid Indian Passport / School Leaving Certificate",
  "Domicile Certificate",
  "HSC Marksheet (Class 12th)",
  "SSC Passing Certificate (Date of Birth Proof)",
  "Medical Fitness Certificate (Annexure-H)"
];

const CATEGORY_DOCS = {
  OPEN: [...BASE_DOCS],
  SC: [...BASE_DOCS, "Caste Certificate (SC)", "Caste Validity Certificate"],
  ST: [...BASE_DOCS, "Caste Certificate (ST)", "Caste Validity Certificate"],
  VJA: [...BASE_DOCS, "Caste Certificate (VJA)", "Caste Validity Certificate", "Non-Creamy Layer (NCL) Certificate (Minimum Valid up to 31/03/2027)"],
  NT1: [...BASE_DOCS, "Caste Certificate (NT1 – B)", "Caste Validity Certificate", "Non-Creamy Layer (NCL) Certificate (Minimum Valid up to 31/03/2027)"],
  NT2: [...BASE_DOCS, "Caste Certificate (NT2 – C)", "Caste Validity Certificate", "Non-Creamy Layer (NCL) Certificate (Minimum Valid up to 31/03/2027)"],
  NT3: [...BASE_DOCS, "Caste Certificate (NT3 – D)", "Caste Validity Certificate", "Non-Creamy Layer (NCL) Certificate (Minimum Valid up to 31/03/2027)"],
  OBC: [...BASE_DOCS, "Caste Certificate (OBC)", "Caste Validity Certificate", "Non-Creamy Layer (NCL) Certificate (Minimum Valid up to 31/03/2027)"],
  SBC: [...BASE_DOCS, "Caste Certificate (SBC)", "Caste Validity Certificate", "Non-Creamy Layer (NCL) Certificate (Minimum Valid up to 31/03/2027)"],
  EWS: [...BASE_DOCS, "EWS (Economically Weaker Section) Certificate (Issued on or after 01/04/2026)"],
  SEBC: [...BASE_DOCS, "Caste Certificate (SEBC)", "Caste Validity Certificate", "Non-Creamy Layer (NCL) Certificate (Minimum Valid up to 31/03/2027)"]
};

const RESERVATION_DOCS = {
  DEFENCE1: { label: "DEFENCE-1", docs: ["Ex-Servicemen Certificate", "Domicile Certificate of Defence Person"] },
  DEFENCE2: { label: "DEFENCE-2", docs: ["Actual Service Certificate", "Domicile Certificate of Defence Person"] },
  DEFENCE3: { label: "DEFENCE-3", docs: ["Actual Service Certificate", "Transfer Certificate", "Domicile Certificate of Defence Person"] },
  MKB: { label: "MKB", docs: ["Dispute Area Certificate (Annexure-E)", "Mother Tongue Certificate (Annexure-E)"] },
  HILLY: { label: "HILLY AREA", docs: ["Hilly Area Certificate", "Parent's Domicile Certificate (Hilly Area)", "Proof of 10th / 12th Passing from Hilly Area"] },
  PWD: { label: "PWD", docs: ["PWD Certificate (Annexure-D)"] },
  ORPHAN: { label: "ORPHAN", docs: ["Certificate from Women & Child Welfare Department"] },
  MINORITY: { label: "MINORITY", docs: ["Minority Affidavit"] }
};

const CAT_LABELS = {
  OPEN: 'OPEN', SC: 'SC', ST: 'ST', VJA: 'VJA',
  NT1: 'NT1 (B)', NT2: 'NT2 (C)', NT3: 'NT3 (D)',
  OBC: 'OBC', SBC: 'SBC', EWS: 'EWS', SEBC: 'SEBC'
};

const cardStyle = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 20 };

export default function PublicAdminDocumentChecklist() {
  const { admin } = usePublicAdmin();
  
  // Form input state
  const [formData, setFormData] = useState({
    candName: "",
    regNo: "",
    neetAir: "",
    neetScore: "",
    genDate: new Date().toISOString().substring(0, 10),
    category: "",
    reservation: ""
  });

  // Generated checklist state
  const [isGenerated, setIsGenerated] = useState(false);
  const [checklistDocs, setChecklistDocs] = useState([]);
  const [checkedState, setCheckedState] = useState({});
  const pdfRef = useRef(null);

  // Auto-set today's date
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      genDate: new Date().toISOString().substring(0, 10)
    }));
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleGenerate = (e) => {
    e.preventDefault();
    if (!formData.category) {
      toast.error("Please select a Main Category first.");
      return;
    }

    const catDocs = CATEGORY_DOCS[formData.category] || [];
    const resDocs = formData.reservation ? (RESERVATION_DOCS[formData.reservation]?.docs || []) : [];

    // Deduplicate special reservation docs already in category docs
    const uniqueResDocs = resDocs.filter(
      d => !catDocs.map(x => x.toLowerCase()).includes(d.toLowerCase())
    );

    const merged = [
      ...catDocs.map(name => ({ name, type: "category" })),
      ...uniqueResDocs.map(name => ({ name, type: "reservation" }))
    ];

    setChecklistDocs(merged);
    
    // Reset checked state for all new indices
    const initialCheckState = {};
    merged.forEach((_, idx) => {
      initialCheckState[idx] = false;
    });
    setCheckedState(initialCheckState);
    setIsGenerated(true);
    toast.success("Document checklist generated!");
  };

  const handleCheckboxChange = (idx) => {
    setCheckedState(prev => {
      const newVal = !prev[idx];
      const docName = checklistDocs[idx]?.name || "Document";
      if (newVal) {
        toast.success(`Verified: ${docName}`);
      } else {
        toast.info(`Unverified: ${docName}`);
      }
      return { ...prev, [idx]: newVal };
    });
  };

  const handleVerifyAll = () => {
    const total = checklistDocs.length;
    const verifiedCount = Object.values(checkedState).filter(Boolean).length;
    const allChecked = verifiedCount === total;

    const newState = {};
    checklistDocs.forEach((_, idx) => {
      newState[idx] = !allChecked;
    });
    setCheckedState(newState);
    if (!allChecked) {
      toast.success("All documents marked as verified!");
    } else {
      toast.info("Cleared all verifications.");
    }
  };

  const handleReset = () => {
    setFormData({
      candName: "",
      regNo: "",
      neetAir: "",
      neetScore: "",
      genDate: new Date().toISOString().substring(0, 10),
      category: "",
      reservation: ""
    });
    setIsGenerated(false);
    setChecklistDocs([]);
    setCheckedState({});
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    toast.success("Generating A4 verification PDF...");
    const element = pdfRef.current;
    if (!element) return;

    // Temporarily make visible for capture
    element.style.display = 'block';

    const opt = {
      margin: [0.4, 0.4, 0.4, 0.4],
      filename: `AME_Checklist_${formData.candName.replace(/\s+/g, '_') || 'Candidate'}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['css', 'legacy'] }
    };

    html2pdf().set(opt).from(element).save().then(() => {
      element.style.display = 'none';
    }).catch(err => {
      console.error(err);
      toast.error("Failed to generate PDF. Please try again.");
      element.style.display = 'none';
    });
  };

  // Stats calculation
  const totalCount = checklistDocs.length;
  const verifiedCount = Object.values(checkedState).filter(Boolean).length;
  const progressPercent = totalCount > 0 ? Math.round((verifiedCount / totalCount) * 100) : 0;
  const allChecked = totalCount > 0 && verifiedCount === totalCount;

  // Filter lists
  const catDocsList = checklistDocs.filter(d => d.type === "category");
  const resDocsList = checklistDocs.filter(d => d.type === "reservation");

  return (
    <div className="space-y-6">
      
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 no-print">
        <div>
          <h1 className="text-2xl font-black tracking-tight" style={{ color: '#f8fafc' }}>
            Document Checklist Generator
          </h1>
          <p className="text-sm mt-1" style={{ color: '#64748b' }}>
            Generate &amp; verify NEET UG 2026 required certificates based on category and reservation.
          </p>
        </div>
        {isGenerated && (
          <button 
            onClick={() => setIsGenerated(false)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold transition-all hover:scale-105 active:scale-95 text-slate-400 bg-white/5 border border-white/10 hover:text-white"
          >
            <ChevronLeft className="h-4 w-4" />
            Go Back / Edit Info
          </button>
        )}
      </div>

      {/* INPUT FORM VIEW */}
      {!isGenerated ? (
        <div className="max-w-3xl p-8 space-y-6 overflow-hidden no-print" style={cardStyle}>
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <FileText className="h-5 w-5 text-amber-500" />
              Candidate Admission Info
            </h2>
            <p className="text-xs text-slate-400 mt-1">
              Select main category and reservation to automatically determine the necessary documents.
            </p>
          </div>

          <form onSubmit={handleGenerate} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              
              {/* Full Name */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Candidate Name</label>
                <div className="relative group">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500 group-focus-within:text-amber-500 transition-colors" />
                  <input
                    name="candName"
                    className="w-full h-12 pl-12 pr-4 rounded-xl bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-amber-500 transition-all"
                    placeholder="e.g. Aditi Deshmukh"
                    value={formData.candName}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* Registration No */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Registration ID / Roll No</label>
                <div className="relative group">
                  <Hash className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500 group-focus-within:text-amber-500 transition-colors" />
                  <input
                    name="regNo"
                    className="w-full h-12 pl-12 pr-4 rounded-xl bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-amber-500 transition-all"
                    placeholder="e.g. 26041098234"
                    value={formData.regNo}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* NEET AIR */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">NEET AIR (All India Rank)</label>
                <div className="relative group">
                  <Award className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500 group-focus-within:text-amber-500 transition-colors" />
                  <input
                    name="neetAir"
                    type="number"
                    className="w-full h-12 pl-12 pr-4 rounded-xl bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-amber-500 transition-all"
                    placeholder="e.g. 14205"
                    value={formData.neetAir}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* NEET Score */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">NEET Score (Out of 720)</label>
                <div className="relative group">
                  <Award className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500 group-focus-within:text-amber-500 transition-colors" />
                  <input
                    name="neetScore"
                    type="number"
                    max="720"
                    className="w-full h-12 pl-12 pr-4 rounded-xl bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-amber-500 transition-all"
                    placeholder="e.g. 642"
                    value={formData.neetScore}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* Category */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Main Category *</label>
                <select
                  required
                  name="category"
                  className="w-full h-12 px-4 rounded-xl bg-[#1e293b] border border-white/10 text-sm text-white focus:outline-none focus:border-amber-500 transition-all"
                  value={formData.category}
                  onChange={handleInputChange}
                >
                  <option value="">-- Select Category --</option>
                  {Object.keys(CATEGORY_DOCS).map(cat => (
                    <option key={cat} value={cat}>{CAT_LABELS[cat] || cat}</option>
                  ))}
                </select>
              </div>

              {/* Special Reservation */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Special Reservation</label>
                <select
                  name="reservation"
                  className="w-full h-12 px-4 rounded-xl bg-[#1e293b] border border-white/10 text-sm text-white focus:outline-none focus:border-amber-500 transition-all"
                  value={formData.reservation}
                  onChange={handleInputChange}
                >
                  <option value="">None / Not Applicable</option>
                  {Object.entries(RESERVATION_DOCS).map(([key, meta]) => (
                    <option key={key} value={key}>{meta.label}</option>
                  ))}
                </select>
              </div>

              {/* Generation Date */}
              <div className="space-y-1.5 md:col-span-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Checklist Verification Date</label>
                <div className="relative group">
                  <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500 group-focus-within:text-amber-500 transition-colors" />
                  <input
                    name="genDate"
                    type="date"
                    className="w-full h-12 pl-12 pr-4 rounded-xl bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-amber-500 transition-all"
                    value={formData.genDate}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

            </div>

            <button
              type="submit"
              className="w-full h-14 rounded-2xl font-black text-white flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-95 mt-4"
              style={{ background: 'linear-gradient(135deg, #f39c12, #d35400)', boxShadow: '0 8px 25px rgba(243,156,18,0.3)' }}
            >
              <FileText className="h-5 w-5" />
              Generate Checklist
            </button>
          </form>
        </div>
      ) : (
        /* INTERACTIVE CHECKLIST VIEW */
        <div className="space-y-6">
          
          {/* STATS / PROGRESS BAR */}
          <div className="p-6 flex flex-col md:flex-row md:items-center justify-between gap-6 no-print" style={{ ...cardStyle, background: 'linear-gradient(135deg, rgba(15,23,42,0.6) 0%, rgba(30,41,59,0.6) 100%)' }}>
            <div className="flex items-center gap-4">
              <div className="text-center bg-amber-500/10 border border-amber-500/20 px-4 py-2.5 rounded-2xl flex-shrink-0">
                <span className="block text-2xl font-black text-[#f39c12] leading-none">{verifiedCount}</span>
                <span className="text-[10px] font-black uppercase text-slate-500 tracking-wider">Verified</span>
              </div>
              <div className="flex-1">
                <div className="flex justify-between items-center text-xs font-bold text-slate-300">
                  <span>Document Completion</span>
                  <span>{progressPercent}% Complete</span>
                </div>
                <div className="w-full h-2.5 bg-white/10 rounded-full overflow-hidden mt-1.5">
                  <div 
                    className="h-full rounded-full transition-all duration-300"
                    style={{ width: `${progressPercent}%`, background: 'linear-gradient(90deg, #f39c12, #d35400)' }}
                  />
                </div>
              </div>
            </div>
            
            {/* Actions Panel */}
            <div className="flex flex-wrap items-center gap-2.5">
              <button 
                onClick={handleVerifyAll}
                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all border flex items-center gap-1.5 ${
                  allChecked 
                    ? 'bg-rose-500/10 text-rose-400 border-rose-500/20 hover:bg-rose-500/20' 
                    : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/20'
                }`}
              >
                {allChecked ? <><X className="h-3.5 w-3.5" /> Clear All</> : <><Check className="h-3.5 w-3.5" /> Verify All</>}
              </button>
              
              <button 
                onClick={handleDownloadPDF}
                className="px-4 py-2 rounded-xl text-xs font-bold transition-all bg-amber-500 text-slate-950 hover:bg-amber-600 flex items-center gap-1.5"
              >
                <Download className="h-3.5 w-3.5" />
                Download PDF
              </button>

              <button 
                onClick={handlePrint}
                className="px-4 py-2 rounded-xl text-xs font-bold transition-all bg-white/5 border border-white/10 hover:text-white flex items-center gap-1.5"
              >
                <Printer className="h-3.5 w-3.5" />
                Print
              </button>

              <button 
                onClick={handleReset}
                className="px-4 py-2 rounded-xl text-xs font-bold transition-all bg-rose-500/10 text-rose-500 border border-rose-500/20 hover:bg-rose-500/20 flex items-center gap-1.5"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Reset
              </button>
            </div>
          </div>

          {/* CANDIDATE INFO STRIP */}
          <div className="p-5 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 text-xs no-print" style={cardStyle}>
            <div>
              <span className="block text-[10px] uppercase font-bold text-slate-500 tracking-wider">Candidate Name</span>
              <span className="font-extrabold text-white text-sm block mt-0.5 truncate">{formData.candName || "—"}</span>
            </div>
            <div>
              <span className="block text-[10px] uppercase font-bold text-slate-500 tracking-wider">Registration ID</span>
              <span className="font-extrabold text-white text-sm block mt-0.5 truncate">{formData.regNo || "—"}</span>
            </div>
            <div>
              <span className="block text-[10px] uppercase font-bold text-slate-500 tracking-wider">NEET AIR</span>
              <span className="font-extrabold text-white text-sm block mt-0.5 truncate">{formData.neetAir || "—"}</span>
            </div>
            <div>
              <span className="block text-[10px] uppercase font-bold text-slate-500 tracking-wider">NEET Score</span>
              <span className="font-extrabold text-white text-sm block mt-0.5 truncate">{formData.neetScore || "—"} / 720</span>
            </div>
            <div>
              <span className="block text-[10px] uppercase font-bold text-slate-500 tracking-wider">Category</span>
              <span className="font-extrabold text-amber-500 text-sm block mt-0.5">{CAT_LABELS[formData.category] || formData.category}</span>
            </div>
            <div>
              <span className="block text-[10px] uppercase font-bold text-slate-500 tracking-wider">Special Reservation</span>
              <span className="font-extrabold text-blue-400 text-sm block mt-0.5">{formData.reservation ? RESERVATION_DOCS[formData.reservation]?.label : "None"}</span>
            </div>
            <div>
              <span className="block text-[10px] uppercase font-bold text-slate-500 tracking-wider">Verified Date</span>
              <span className="font-extrabold text-white text-sm block mt-0.5">{new Date(formData.genDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
            </div>
          </div>

          {/* CHECKLIST TABLES SCREEN VIEW */}
          <div className="space-y-6 no-print">
            {/* Category Documents Table */}
            {catDocsList.length > 0 && (
              <div className="p-6 space-y-4" style={cardStyle}>
                <h3 className="font-bold text-base text-white border-b border-white/5 pb-2.5 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-amber-500" />
                  Category Documents
                  <span className="text-[10px] bg-amber-500/10 text-amber-500 border border-amber-500/20 px-2 py-0.5 rounded-full uppercase tracking-wider font-extrabold ml-1">
                    {CAT_LABELS[formData.category] || formData.category}
                  </span>
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="border-b border-white/5 text-slate-400">
                        <th className="py-3 px-4 font-bold uppercase tracking-wider w-16">Sr.</th>
                        <th className="py-3 px-4 font-bold uppercase tracking-wider">Document Required</th>
                        <th className="py-3 px-4 font-bold uppercase tracking-wider text-center w-28">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {checklistDocs.map((doc, idx) => {
                        if (doc.type !== "category") return null;
                        const isVerified = !!checkedState[idx];
                        return (
                          <tr 
                            key={idx}
                            onClick={() => handleCheckboxChange(idx)}
                            className={`border-b border-white/5 hover:bg-white/[0.02] cursor-pointer transition-colors ${isVerified ? 'bg-emerald-500/5' : ''}`}
                          >
                            <td className="py-3 px-4 text-slate-500 font-bold">{idx + 1}</td>
                            <td className={`py-3 px-4 font-semibold ${isVerified ? 'text-emerald-400 line-through opacity-75' : 'text-slate-200'}`}>{doc.name}</td>
                            <td className="py-3 px-4 text-center">
                              <input
                                type="checkbox"
                                className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-amber-500 focus:ring-amber-500 focus:ring-offset-slate-900 cursor-pointer"
                                checked={isVerified}
                                onChange={() => {}} // handled by row onClick
                                onClick={(e) => e.stopPropagation()} // prevent double toggles
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Special Reservation Documents Table */}
            {resDocsList.length > 0 && (
              <div className="p-6 space-y-4" style={cardStyle}>
                <h3 className="font-bold text-base text-white border-b border-white/5 pb-2.5 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-blue-400" />
                  Special Reservation Documents
                  <span className="text-[10px] bg-blue-500/10 text-blue-400 border border-blue-500/20 px-2 py-0.5 rounded-full uppercase tracking-wider font-extrabold ml-1">
                    {RESERVATION_DOCS[formData.reservation]?.label}
                  </span>
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="border-b border-white/5 text-slate-400">
                        <th className="py-3 px-4 font-bold uppercase tracking-wider w-16">Sr.</th>
                        <th className="py-3 px-4 font-bold uppercase tracking-wider">Document Required</th>
                        <th className="py-3 px-4 font-bold uppercase tracking-wider text-center w-28">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {checklistDocs.map((doc, idx) => {
                        if (doc.type !== "reservation") return null;
                        const isVerified = !!checkedState[idx];
                        return (
                          <tr 
                            key={idx}
                            onClick={() => handleCheckboxChange(idx)}
                            className={`border-b border-white/5 hover:bg-white/[0.02] cursor-pointer transition-colors ${isVerified ? 'bg-emerald-500/5' : ''}`}
                          >
                            <td className="py-3 px-4 text-slate-500 font-bold">{idx + 1}</td>
                            <td className={`py-3 px-4 font-semibold ${isVerified ? 'text-emerald-400 line-through opacity-75' : 'text-slate-200'}`}>{doc.name}</td>
                            <td className="py-3 px-4 text-center">
                              <input
                                type="checkbox"
                                className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-amber-500 focus:ring-amber-500 focus:ring-offset-slate-900 cursor-pointer"
                                checked={isVerified}
                                onChange={() => {}} // handled by row onClick
                                onClick={(e) => e.stopPropagation()} // prevent double toggles
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* HIDDEN TEMPLATE FOR PRINTING & PDF EXPORT */}
      <div style={{ display: 'none' }}>
        <div 
          ref={pdfRef} 
          style={{ 
            background: 'white', 
            color: 'black', 
            fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", 
            padding: '24px', 
            maxWidth: '800px', 
            margin: '0 auto',
            position: 'relative'
          }}
        >
          {/* Watermark Logo in Background */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            opacity: 0.05,
            width: '400px',
            height: '400px',
            zIndex: 0,
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <img src="/logo.png" alt="watermark" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>

          <div style={{ position: 'relative', zIndex: 1 }}>
            {/* Header / Branding */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2.5px solid #0B1F45', paddingBottom: '12px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <img src="/logo.png" alt="logo" style={{ width: '48px', height: '48px', objectFit: 'contain' }} />
                <div>
                  <h1 style={{ fontSize: '20px', fontWeight: '800', color: '#0B1F45', margin: 0, letterSpacing: '-0.3px' }}>
                    Admissions Made Easy
                  </h1>
                  <p style={{ fontSize: '11px', color: '#C8973A', margin: '2px 0 0 0', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    NEET-UG 2026 Document Verification Checklist
                  </p>
                </div>
              </div>
              <div style={{ textAlign: 'right', fontSize: '9px', color: '#64748b', fontWeight: 'bold' }}>
                Verified on: {new Date(formData.genDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
              </div>
            </div>

            {/* Profile Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', background: '#FAF7F2', border: '1px solid #d8d5ce', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px', fontSize: '11px' }}>
              <div>
                <strong style={{ color: '#8a8a9a', fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Candidate Name</strong>
                <div style={{ fontWeight: 'bold', color: '#0B1F45', marginTop: '2px' }}>{formData.candName || "—"}</div>
              </div>
              <div>
                <strong style={{ color: '#8a8a9a', fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Registration ID</strong>
                <div style={{ fontWeight: 'bold', color: '#0B1F45', marginTop: '2px' }}>{formData.regNo || "—"}</div>
              </div>
              <div>
                <strong style={{ color: '#8a8a9a', fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>NEET AIR</strong>
                <div style={{ fontWeight: 'bold', color: '#0B1F45', marginTop: '2px' }}>{formData.neetAir || "—"}</div>
              </div>
              <div>
                <strong style={{ color: '#8a8a9a', fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>NEET Score</strong>
                <div style={{ fontWeight: 'bold', color: '#0B1F45', marginTop: '2px' }}>{formData.neetScore ? `${formData.neetScore} / 720` : "—"}</div>
              </div>
              <div>
                <strong style={{ color: '#8a8a9a', fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Category</strong>
                <div style={{ fontWeight: 'bold', color: '#C8973A', marginTop: '2px' }}>{CAT_LABELS[formData.category] || formData.category || "—"}</div>
              </div>
              <div>
                <strong style={{ color: '#8a8a9a', fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Special Reservation</strong>
                <div style={{ fontWeight: 'bold', color: '#162d5e', marginTop: '2px' }}>{formData.reservation ? RESERVATION_DOCS[formData.reservation]?.label : "None"}</div>
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <strong style={{ color: '#8a8a9a', fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Counselor Verified</strong>
                <div style={{ fontWeight: 'bold', color: '#1a6b3c', marginTop: '2px' }}>{admin?.name} ({admin?.email})</div>
              </div>
            </div>

            {/* Checklist Tables */}
            {catDocsList.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '12px', fontWeight: '800', color: '#0B1F45', marginBottom: '8px', borderBottom: '1.5px solid #C8973A', paddingBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Category Documents ({CAT_LABELS[formData.category] || formData.category})
                </h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10.5px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#0B1F45', color: 'white' }}>
                      <th style={{ padding: '6px 8px', fontWeight: 'bold', textAlign: 'center', width: '35px' }}>Sr.</th>
                      <th style={{ padding: '6px 8px', fontWeight: 'bold', textAlign: 'left' }}>Required Document Name</th>
                      <th style={{ padding: '6px 8px', fontWeight: 'bold', textAlign: 'center', width: '80px' }}>Verified ✓</th>
                    </tr>
                  </thead>
                  <tbody>
                    {checklistDocs.map((doc, idx) => {
                      if (doc.type !== "category") return null;
                      const isVerified = !!checkedState[idx];
                      return (
                        <tr key={idx} style={{ borderBottom: '1px solid #d8d5ce', backgroundColor: isVerified ? '#f0faf4' : 'transparent' }}>
                          <td style={{ padding: '6px 8px', textAlign: 'center', color: '#8a8a9a', fontWeight: 'bold' }}>{idx + 1}</td>
                          <td style={{ padding: '6px 8px', color: isVerified ? '#1a6b3c' : '#1a1a2e', fontWeight: isVerified ? 'bold' : 'normal' }}>{doc.name}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: '12px', color: '#1a6b3c', fontWeight: 'bold' }}>
                            {isVerified ? "✔" : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* resDocsList Table */}
            {resDocsList.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '12px', fontWeight: '800', color: '#0B1F45', marginBottom: '8px', borderBottom: '1.5px solid #C8973A', paddingBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Special Reservation Documents ({RESERVATION_DOCS[formData.reservation]?.label})
                </h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10.5px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#0B1F45', color: 'white' }}>
                      <th style={{ padding: '6px 8px', fontWeight: 'bold', textAlign: 'center', width: '35px' }}>Sr.</th>
                      <th style={{ padding: '6px 8px', fontWeight: 'bold', textAlign: 'left' }}>Required Document Name</th>
                      <th style={{ padding: '6px 8px', fontWeight: 'bold', textAlign: 'center', width: '80px' }}>Verified ✓</th>
                    </tr>
                  </thead>
                  <tbody>
                    {checklistDocs.map((doc, idx) => {
                      if (doc.type !== "reservation") return null;
                      const isVerified = !!checkedState[idx];
                      return (
                        <tr key={idx} style={{ borderBottom: '1px solid #d8d5ce', backgroundColor: isVerified ? '#f0faf4' : 'transparent' }}>
                          <td style={{ padding: '6px 8px', textAlign: 'center', color: '#8a8a9a', fontWeight: 'bold' }}>{idx + 1}</td>
                          <td style={{ padding: '6px 8px', color: isVerified ? '#1a6b3c' : '#1a1a2e', fontWeight: isVerified ? 'bold' : 'normal' }}>{doc.name}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: '12px', color: '#1a6b3c', fontWeight: 'bold' }}>
                            {isVerified ? "✔" : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Bottom Signatures */}
            <div style={{ marginTop: '40px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '50px', fontSize: '11px', pageBreakInside: 'avoid' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ height: '35px' }}></div>
                <div style={{ borderTop: '1.5px solid #1a1a2e', paddingTop: '6px', fontWeight: 'bold', color: '#0B1F45' }}>
                  Candidate Signature
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ height: '35px', fontSize: '12px', fontStyle: 'italic', color: '#8a8a9a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  Verified Digitally
                </div>
                <div style={{ borderTop: '1.5px solid #1a1a2e', paddingTop: '6px', fontWeight: 'bold', color: '#0B1F45' }}>
                  Counselor Signature
                </div>
              </div>
            </div>
            
            {/* Disclaimer */}
            <div style={{ marginTop: '24px', borderTop: '1px dashed #d8d5ce', paddingTop: '10px', fontSize: '9px', color: '#8a8a9a', fontStyle: 'italic', lineHeight: '1.4', pageBreakInside: 'avoid' }}>
              Disclaimer: This verification checklist is dynamically generated based on details submitted by the counselor. Please keep original files and 3 sets of attested photocopies ready for state reporting.
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
